/**
 * ContentBlockRenderer - Renders content blocks based on type
 *
 * Main dispatcher that renders the appropriate component for each of the
 * 34 content block types. Supports flat blocks, nested CONTAINER/COLUMNS
 * layouts, SCROLL_NAV anchoring, and entity reference blocks (COMPETITION,
 * QUEST, and 5 brand activation types).
 *
 * Each block is wrapped in a design container that applies per-block
 * visual controls (background, padding, borders, scroll animations).
 */

import { useRef, useEffect, useState, useMemo, CSSProperties } from "react";
import { SurfaceContentBlockResponse, SurfaceBlockDesign, SurfaceThemeColors } from "@/hooks/hub/useGetHub";
import { HtmlBlock } from "@/widgets/HtmlBlock";
import { PromoSection } from "@/widgets/PromoSection";
import { TextImageBlock } from "@/widgets/TextImageBlock";
import { CountdownBlock } from "@/widgets/CountdownBlock";
import { SponsorsBlock } from "@/widgets/SponsorsBlock";
import { LiveBracketBlock } from "@/widgets/LiveBracketBlock";
import { StepsBlock } from "@/widgets/StepsBlock";
import { PrizesBlock } from "@/widgets/PrizesBlock";
import { FaqBlock } from "@/widgets/FaqBlock";
import { LeaderboardBlock } from "@/widgets/LeaderboardBlock";
import { CtaBannerBlock } from "@/widgets/CtaBannerBlock";
import { VideoBlock } from "@/widgets/VideoBlock";
import { RewardsSection } from "@/widgets/RewardsSection";
import { ProductsSection } from "@/widgets/ProductsSection";
import { ImageGalleryBlock } from "@/widgets/ImageGalleryBlock";
import { StatsBlock } from "@/widgets/StatsBlock";
import { SocialEmbedBlock } from "@/widgets/SocialEmbedBlock";
import { StreamingBlock } from "@/widgets/StreamingBlock";
import { NotificationSignupBlock } from "@/widgets/NotificationSignupBlock";
import { PollBlock } from "@/widgets/PollBlock";
import { ScrollNavBlock } from "@/widgets/ScrollNavBlock";
import { StoreLocatorBlock } from "@/widgets/StoreLocatorBlock";
import { MyPositionBlock } from "@/widgets/MyPositionBlock";
import { CompetitionBlock } from "@/widgets/CompetitionBlock";
import { JourneyRoadBlock } from "./JourneyRoadBlock";
import { JourneyTrackBand } from "./JourneyTrackBand";
import { QuestBlock } from "@/widgets/QuestBlock";
import { ActivationBlock } from "@/widgets/ActivationBlock";
import { AchievementsBlock } from "@/widgets/AchievementsBlock";
import { EarningBlock } from "@/widgets/EarningBlock";
import { OnboardingBlock } from "@/widgets/OnboardingBlock";
import { CodeEntryBlock } from "@/widgets/CodeEntryBlock";
import { GamificationRenderer } from "@/components/gamification/GamificationRenderer";
import { LootBoxBlock } from "@/widgets/LootBoxBlock";
import { StreamZoneBlock } from "@/widgets/StreamZoneBlock";
import { ChallengeZoneBlock } from "@/widgets/ChallengeZoneBlock";
import "./ContentBlockRenderer.scss";
/*
 * NOTE: HubRewardsSection and SurfaceProductsSection are now dynamically rendered
 * from rewardsConfig/productsConfig in contentBlocks[]. Monster Energy hub
 * still hardcodes them directly in HubPage.tsx as a fallback.
 */

interface HubPointDisplay {
    pointsName: string;
    currentPoints: number;
    currentLevel: number;
    nextLevelPoints: number;
}

interface ContentBlockRendererProps {
    blocks?: SurfaceContentBlockResponse[];
    themeColors?: SurfaceThemeColors;
    brandName?: string;
    /** Hub slug — passed to HubLeaderboardBlock for hub points source */
    surfaceSlug?: string;
    /** Hub point display data — passed to REWARDS block */
    pointDisplay?: HubPointDisplay;
    /** Hub lifecycle phase — used to filter blocks by visiblePhase */
    hubPhase?: "pre_launch" | "live";
    /**
     * The board this surface places, when it places one. The earn section
     * draws its cards from the board's assigned objectives — the surface
     * already made that assignment, so nothing restates it on the block.
     */
    ledgerBoardId?: string;
}

/**
 * Renders all enabled content blocks in order
 */
export const ContentBlockRenderer = ({
    blocks,
    themeColors,
    brandName,
    surfaceSlug,
    pointDisplay,
    hubPhase,
    ledgerBoardId,
}: ContentBlockRendererProps) => {
    /*
     * Re-evaluate scheduled blocks every 60 seconds so showAfter/showUntil
     * blocks appear/disappear without requiring a page refresh or data refetch.
     */
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const hasScheduledBlocks = blocks?.some(b => b.showAfter || b.showUntil);
        if (!hasScheduledBlocks) return;
        const interval = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(interval);
    }, [blocks]);

    if (!blocks || blocks.length === 0) return null;

    // Filter enabled blocks, apply scheduling and phase visibility, and sort by order
    const enabledBlocks = blocks
        .filter(block => {
            if (!block.enabled) return false;
            // Block scheduling: hide blocks outside their time window
            if (block.showAfter && now < block.showAfter) return false;
            if (block.showUntil && now > block.showUntil) return false;
            /*
             * Phase-based visibility: "always" passes regardless of hub phase;
             * "pre_launch"/"live" only passes when hub phase matches
             */
            const vp = block.visiblePhase || "always";
            if (vp !== "always" && hubPhase && vp !== hubPhase) return false;
            return true;
        })
        .sort((a, b) => a.order - b.order);

    if (enabledBlocks.length === 0) return null;

    return (
        <div className="content-blocks">
            {enabledBlocks.map(block =>
                <SurfaceContentBlock
                    key={block._id}
                    block={block}
                    themeColors={themeColors}
                    brandName={brandName}
                    surfaceSlug={surfaceSlug}
                    pointDisplay={pointDisplay}
                    ledgerBoardId={ledgerBoardId}
                />)}
        </div>
    );
};

/*
 * ============================================
 * Design Helpers
 * ============================================
 */

/** Allowlists for class-name interpolation — defense-in-depth against CSS class injection */
const VALID_PADDING = new Set(["none", "sm", "md", "lg", "xl"]);
const VALID_ANIMATE = new Set(["none", "fade-up", "fade-in", "slide-left", "slide-right"]);
const VALID_ALIGN = new Set(["left", "center", "right"]);

/**
 * Only safe CSS gradient functions are accepted. Prevents CSS injection
 * via raw strings assigned to backgroundImage.
 */
const SAFE_GRADIENT_RE = /^(linear|radial|conic)-gradient\(/;

/** Only allow https:// URLs for background images — prevents CSS injection via url() breakout */
const SAFE_URL_RE = /^https:\/\//;

function buildBlockDesignStyles(design?: SurfaceBlockDesign): CSSProperties {
    if (!design) return {};

    const styles: CSSProperties = {};

    // Priority: gradient > image > color. Only one background applied.
    if (design.backgroundGradient && SAFE_GRADIENT_RE.test(design.backgroundGradient.trim())) {
        styles.backgroundImage = design.backgroundGradient;
    } else if (design.backgroundImageUrl && SAFE_URL_RE.test(design.backgroundImageUrl.trim())) {
        styles.backgroundImage = `url(${design.backgroundImageUrl})`;
        styles.backgroundSize = "cover";
        styles.backgroundPosition = "center";
    } else if (design.backgroundColor) {
        // Validate CSS color value — consistent with SAFE_GRADIENT_RE and SAFE_URL_RE guards above
        const SAFE_COLOR_RE = /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-zA-Z]+)$/;
        if (SAFE_COLOR_RE.test(design.backgroundColor.trim())) {
            styles.backgroundColor = design.backgroundColor;
        }
    }

    if (design.textAlignment && VALID_ALIGN.has(design.textAlignment)) {
        styles.textAlign = design.textAlignment;
    }

    return styles;
}

function buildBlockDesignClasses(design?: SurfaceBlockDesign, isVisible?: boolean): string {
    if (!design) return "";

    const classes: string[] = [];

    if (design.fullBleed) classes.push("content-block--full-bleed");
    if (design.borderTop) classes.push("content-block--border-top");
    if (design.borderBottom) classes.push("content-block--border-bottom");
    if (design.roundedCorners === true) classes.push("content-block--rounded");

    const padding = VALID_PADDING.has(design.padding as string) ? design.padding : "md";
    classes.push(`content-block--padding-${padding}`);

    if (design.animateOnScroll && VALID_ANIMATE.has(design.animateOnScroll) && design.animateOnScroll !== "none") {
        classes.push(`content-block--animate-${design.animateOnScroll}`);
        if (isVisible) classes.push("content-block--visible");
    }

    return classes.join(" ");
}

/**
 * Determines whether a blockDesign has any visual effect worth wrapping.
 * Returns false for empty/defaults-only objects to avoid double-padding
 * with inner block components that have their own padding.
 */
function hasVisualDesign(design?: SurfaceBlockDesign): boolean {
    if (!design) return false;
    return Boolean(design.backgroundColor ||
        design.backgroundGradient ||
        design.backgroundImageId ||
        design.fullBleed ||
        design.borderTop ||
        design.borderBottom ||
        design.animateOnScroll && design.animateOnScroll !== "none" ||
        design.textAlignment && design.textAlignment !== "left" ||
        design.padding && design.padding !== "md" ||
        design.roundedCorners === true);
}

/**
 * Custom hook: IntersectionObserver-based scroll reveal.
 * Respects prefers-reduced-motion — returns true immediately if user prefers reduced motion.
 * threshold: 0.15 — fires when 15% of element enters viewport, giving early-enough
 * trigger for the 0.6s CSS transition to complete before the element is fully in view.
 */
function useScrollReveal(ref: React.RefObject<HTMLDivElement | null>, animationType?: string): boolean {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!animationType || animationType === "none") {
            setIsVisible(true);
            return;
        }

        // Respect prefers-reduced-motion
        if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            setIsVisible(true);
            return;
        }

        const element = ref.current;
        if (!element) return;

        // If element is already in viewport, skip animation setup
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
            setIsVisible(true);
            return;
        }

        // Reset for new animation when element is off-screen
        setIsVisible(false);

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(element);
                }
            },
            { threshold: 0.15 },
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [ref, animationType]);

    return isVisible;
}

/*
 * ============================================
 * Layout Helpers
 * ============================================
 */

const COLUMN_SPLIT_MAP: Record<string, string> = {
    "50-50": "1fr 1fr",
    "60-40": "3fr 2fr",
    "70-30": "7fr 3fr",
    "40-60": "2fr 3fr",
    "30-70": "3fr 7fr",
};

function buildLayoutStyles(layout?: { columns?: number; columnSplit?: string; stackOnMobile?: boolean }): CSSProperties {
    if (!layout || !layout.columns || layout.columns <= 1) return {};
    const cols = layout.columns;
    if (cols === 2) {
        return { display: "grid", gridTemplateColumns: COLUMN_SPLIT_MAP[layout.columnSplit || "50-50"] || "1fr 1fr", gap: "var(--hub-section-gap, 1.5rem)" };
    }
    if (cols === 3) {
        return { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--hub-section-gap, 1.5rem)" };
    }
    return {};
}

function buildLayoutClasses(layout?: { columns?: number; stackOnMobile?: boolean }): string {
    if (!layout || !layout.columns || layout.columns <= 1) return "";
    const classes = ["content-block--multi-col"];
    if (layout.stackOnMobile !== false) classes.push("content-block--stack-mobile");
    return classes.join(" ");
}

/*
 * ============================================
 * Single Block Renderer
 * ============================================
 */

export interface SurfaceContentBlockProps {
    block: SurfaceContentBlockResponse;
    themeColors?: SurfaceThemeColors;
    brandName?: string;
    surfaceSlug?: string;
    pointDisplay?: HubPointDisplay;
    /** The board this surface places — the earn section draws its cards from it. */
    ledgerBoardId?: string;
}

/**
 * Renders exactly one content block, with its design wrapper and the same
 * type dispatch `ContentBlockRenderer` uses internally. Exported so a
 * structure-aware page (HUB) can place ONE block per slot via the
 * occupancy/slot system instead of `ContentBlockRenderer`'s own flat,
 * unstructured `.map()` — the placement decision belongs to the structure
 * layer (`dropSlots.ts#contentBlockSlots`), not to this component.
 */
export const SurfaceContentBlock = ({ block, themeColors, brandName, surfaceSlug, pointDisplay, ledgerBoardId }: SurfaceContentBlockProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useScrollReveal(ref, block.blockDesign?.animateOnScroll);

    const designStyles = buildBlockDesignStyles(block.blockDesign);
    const designClasses = buildBlockDesignClasses(block.blockDesign, isVisible);
    const layoutStyles = useMemo(() => buildLayoutStyles(block.layout), [block.layout]);
    const layoutClasses = useMemo(() => buildLayoutClasses(block.layout), [block.layout]);

    const blockContent = renderBlockContent(block, surfaceSlug, themeColors, brandName, pointDisplay, ledgerBoardId);
    if (!blockContent) return null;

    const hasLayout = block.layout && (block.layout.columns ?? 1) > 1;
    const hasDesign = hasVisualDesign(block.blockDesign);

    /*
     * If no visual design controls and no layout, render block content directly (backwards compatible)
     * Still add data-block-id for scroll nav targeting
     */
    if (!hasDesign && !hasLayout) {
        return <div data-block-id={block.blockId || block._id}>{blockContent}</div>;
    }

    // Overlay only renders when background image is active (gradient takes CSS priority, so skip overlay when gradient is set)
    const showOverlay = !block.blockDesign?.backgroundGradient &&
        block.blockDesign?.backgroundImageUrl &&
        (block.blockDesign?.backgroundOverlayOpacity ?? 0) > 0;

    return (
        <div
            ref={ref}
            data-block-id={block.blockId || block._id}
            className={`content-block ${designClasses} ${layoutClasses}`.trim()}
            style={{ ...designStyles, ...layoutStyles }}
        >
            {showOverlay &&
                <div
                    className="content-block__overlay"
                    style={{ opacity: (block.blockDesign!.backgroundOverlayOpacity ?? 0) / 100 }}
                />
            }
            <div className="content-block__inner">
                {blockContent}
            </div>
        </div>
    );
};

/**
 * Column split CSS grid template mappings for the COLUMNS block.
 * Same mappings as the existing COLUMN_SPLIT_MAP but used for nested columns.
 */
const COLUMNS_SPLIT_GRID: Record<string, string> = {
    "50-50": "1fr 1fr",
    "60-40": "3fr 2fr",
    "70-30": "7fr 3fr",
    "40-60": "2fr 3fr",
    "30-70": "3fr 7fr",
    "33-33-33": "1fr 1fr 1fr",
};

/** Maps a content block's type to the corresponding React component */
function renderBlockContent(block: SurfaceContentBlockResponse, surfaceSlug?: string, themeColors?: SurfaceThemeColors, brandName?: string, pointDisplay?: HubPointDisplay, ledgerBoardId?: string) {
    switch (block.type) {
        case "HTML":
        case "RICH_TEXT":
            return (
                <HtmlBlock
                    content={block.htmlContent}
                    widthPercentage={block.widthPercentage}
                />
            );

        case "PROMO":
            return (
                <PromoSection
                    items={block.promoItems}
                    layout={block.promoLayout}
                />
            );

        case "TEXT_IMAGE":
            return (
                <TextImageBlock
                    textContent={block.textContent}
                    imageUrl={block.imageUrl}
                    imagePosition={block.imagePosition}
                />
            );

        case "REWARDS":
            return (
                <RewardsSection
                    brandName={brandName}
                    pointsName={pointDisplay?.pointsName}
                    currentPoints={pointDisplay?.currentPoints}
                    currentLevel={pointDisplay?.currentLevel}
                    nextLevelPoints={pointDisplay?.nextLevelPoints}
                    rewards={block.rewardsConfig?.rewards?.map((r, i) => ({
                        id: r._id || `reward-${i}`,
                        name: r.name,
                        description: r.description,
                        imageUrl: r.imageUrl,
                        requiredLevel: r.requiredLevel,
                        requiredPoints: r.requiredPoints,
                        bonusText: r.bonusText,
                        category: r.category,
                    }))}
                    heroImageUrl={block.rewardsConfig?.heroImageUrl}
                    howToPlaySteps={block.rewardsConfig?.howToPlaySteps?.map(s => ({
                        title: s.title,
                        description: s.description,
                        icon: s.icon,
                    }))}
                    themeColors={themeColors ? {
                        primary: themeColors.primary,
                        secondary: themeColors.secondary,
                        accent: themeColors.accent,
                    } : undefined}
                />
            );

        case "PRODUCTS":
            return (
                <ProductsSection
                    sectionTitle={block.productsConfig?.sectionTitle}
                    subtitle={block.productsConfig?.subtitle}
                    subtitleUrl={block.productsConfig?.subtitleUrl}
                    products={block.productsConfig?.products?.map((p, i) => ({
                        id: p._id || `product-${i}`,
                        name: p.name || "Product",
                        imageUrl: p.imageUrl || undefined,
                        price: p.price,
                        productUrl: p.productUrl,
                        description: p.description,
                        ctaText: p.ctaText,
                    }))}
                    retailers={block.productsConfig?.retailers?.map((r, i) => ({
                        id: r._id || `retailer-${i}`,
                        name: r.name || "Retailer",
                        logoUrl: r.logoImageUrl || r.logoUrl,
                        productUrl: r.productUrl || "",
                        type: r.type || "online",
                        exclusive: r.exclusive,
                    }))}
                />
            );

        case "COUNTDOWN":
            return (
                <CountdownBlock
                    countdownSource={block.countdownSource}
                    countdownTargetDate={block.countdownTargetDate}
                    countdownLabel={block.countdownLabel}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "SPONSORS":
            return (
                <SponsorsBlock
                    sponsors={block.sponsors}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "LIVE_BRACKET":
            return (
                <LiveBracketBlock
                    bracketCompetitionId={block.bracketCompetitionId}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "STEPS":
            return (
                <StepsBlock
                    config={block.stepsConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "PRIZES":
            return (
                <PrizesBlock
                    config={block.prizesConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "FAQ":
            return (
                <FaqBlock
                    config={block.faqConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "LEADERBOARD":
            return (
                <LeaderboardBlock
                    config={block.leaderboardConfig}
                    sectionTitle={block.sectionTitle}
                    surfaceSlug={surfaceSlug}
                />
            );

        case "CTA_BANNER":
            return (
                <CtaBannerBlock
                    config={block.ctaConfig}
                    sectionTitle={block.sectionTitle}
                    surfaceSlug={surfaceSlug}
                />
            );

        case "VIDEO":
            return (
                <VideoBlock
                    config={block.videoConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "IMAGE_GALLERY":
            return (
                <ImageGalleryBlock
                    config={block.imageGalleryConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "STATS":
            return (
                <StatsBlock
                    config={block.statsConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "SOCIAL_EMBED":
            return (
                <SocialEmbedBlock
                    config={block.socialEmbedConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "STREAMING":
            return (
                <StreamingBlock
                    config={block.streamingConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "NOTIFICATION_SIGNUP":
            return (
                <NotificationSignupBlock
                    config={block.notificationSignupConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "POLL":
            return (
                <PollBlock
                    config={block.pollConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "CONTAINER": {
            const children = block.containerConfig?.children || [];
            if (children.length === 0) return null;
            return (
                <div className="container-block">
                    {children
                        .filter(child => child.enabled)
                        .sort((a, b) => a.order - b.order)
                        .map(child =>
                            <SurfaceContentBlock
                                key={child._id}
                                block={child}
                                themeColors={themeColors}
                                brandName={brandName}
                                surfaceSlug={surfaceSlug}
                                pointDisplay={pointDisplay}
                            />)}
                </div>
            );
        }

        case "COLUMNS": {
            const colConfig = block.columnsConfig;
            const columns = colConfig?.columns || [];
            if (columns.length === 0) return null;
            const gridTemplate = COLUMNS_SPLIT_GRID[colConfig?.columnSplit || "50-50"] || "1fr 1fr";
            const stackMobile = colConfig?.stackOnMobile !== false;
            return (
                <div
                    className={`columns-block ${stackMobile ? "columns-block--stack-mobile" : ""}`}
                    style={{ display: "grid", gridTemplateColumns: gridTemplate, gap: "var(--hub-section-gap, 1.5rem)" }}
                >
                    {columns.map((col, colIndex) =>
                        <div key={colIndex} className="columns-block__column">
                            {(col || [])
                                .filter(child => child.enabled)
                                .sort((a, b) => a.order - b.order)
                                .map(child =>
                                    <SurfaceContentBlock
                                        key={child._id}
                                        block={child}
                                        themeColors={themeColors}
                                        brandName={brandName}
                                        surfaceSlug={surfaceSlug}
                                        pointDisplay={pointDisplay}
                                    />)}
                        </div>)}
                </div>
            );
        }

        case "SCROLL_NAV":
            return (
                <ScrollNavBlock
                    config={block.scrollNavConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "STORE_LOCATOR":
            return (
                <StoreLocatorBlock
                    config={block.storeLocatorConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "MY_POSITION":
            return (
                <MyPositionBlock
                    config={block.myPositionConfig}
                    sectionTitle={block.sectionTitle}
                    surfaceSlug={surfaceSlug}
                />
            );

        case "COMPETITION":
            /*
             * "Journey" renders the authored Journey track as the S-curve
             * band (the anchor's 7-stop section) instead of the entity card.
             * displayMode is a free string, so this routes without a model
             * change — gap-register row 9.
             */
            if (block.displayMode?.toLowerCase() === "journey") {
                return <JourneyTrackBand sectionTitle={block.sectionTitle} />;
            }
            return (
                <CompetitionBlock
                    competitionId={block.competitionId}
                    displayMode={block.displayMode}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "QUEST":
            /*
             * "Journey" mounts The Road — the REAL step ladder
             * (SegmentJourneyPath via deriveSteps), not the link card.
             * Gap-register row 8.
             */
            if (block.displayMode?.toLowerCase() === "journey") {
                return (
                    <JourneyRoadBlock
                        questConfigId={block.questConfigId}
                        sectionTitle={block.sectionTitle}
                    />
                );
            }
            return (
                <QuestBlock
                    questConfigId={block.questConfigId}
                    displayMode={block.displayMode}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "PLAYABLE_AD":
        case "BRAND_CHALLENGE":
        case "QUEST_SPONSORSHIP":
        case "GUILD_SPONSORSHIP":
            return (
                <ActivationBlock
                    activationId={block.activationId}
                    displayMode={block.displayMode}
                    sectionTitle={block.sectionTitle}
                    blockType={block.type}
                />
            );

        case "ACHIEVEMENTS":
            return (
                <AchievementsBlock surfaceSlug={surfaceSlug} sectionTitle={block.sectionTitle} />
            );

        case "ONBOARDING":
            return (
                <OnboardingBlock
                    config={block.onboardingConfig}
                    surfaceSlug={surfaceSlug}
                />
            );

        case "EARNING":
            return (
                <EarningBlock
                    sectionTitle={block.sectionTitle}
                    ledgerBoardId={ledgerBoardId}
                />
            );

        case "CODE_ENTRY":
            return (
                <CodeEntryBlock config={block.codeEntryConfig} />
            );

        case "GAMIFICATION":
            return block.gamificationConfig?.gamificationModelId
                ? <GamificationRenderer
                    gamificationModelId={block.gamificationConfig.gamificationModelId}
                    displayMode={(block.gamificationConfig.displayMode || block.displayMode || "full") as "full" | "compact" | "micro" | "overlay"}
                    sectionTitle={block.sectionTitle}
                />
                : <div className="gamification-block gamification-block--empty">
                    <span className="gamification-block__empty-label">Gamification block — no model configured</span>
                </div>;
        case "LOOTBOX":
            return (
                <LootBoxBlock lootBoxConfigId={block.lootBoxConfigId} />
            );

        case "STREAM_ZONE":
            return (
                <StreamZoneBlock
                    config={block.streamZoneConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        case "CHALLENGE_ZONE":
            return (
                <ChallengeZoneBlock
                    config={block.challengeZoneConfig}
                    sectionTitle={block.sectionTitle}
                />
            );

        default:
            console.warn(`Unknown content block type: ${block.type}`);
            return null;
    }
}
